import React, { useMemo, useState } from "react";
import "./InstallmentsCalculator.css";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Bookmark, Calculator, Download, Euro, Home, KeyRound } from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asText,
  clampNumber,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import {
  GIRONA_LOAN_PARAMS,
  MAX_TERM_YEARS,
  MIN_TERM_YEARS,
  PRODUCTS,
  calculateInstalmentsGirona,
} from "./InstallmentsCalculatorGironaCalc";

function Row({ label, value, className = "" }) {
  return (
    <div className={`simulator__taxes-row ${className}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ProductBox({ product, t, money, percent }) {
  const periodYears = product.reviewFromMonth ? (product.reviewFromMonth - 1) / 12 : null;
  const firstLabel =
    product.key === "fixed" || !product.reviewFromMonth
      ? t("instalment")
      : t(product.key === "variable" ? "instalment-first-year" : "instalment-first-years", {
          years: periodYears,
        });

  return (
    <div className="simulator__taxes-box">
      <span className="simulator__taxes-title">{t(`products.${product.key}`)}</span>
      <Row label={firstLabel} value={money(product.instalmentInitial)} />
      {product.instalmentReview !== null && (
        <Row label={t("instalment-remaining")} value={money(product.instalmentReview)} />
      )}
      <Row
        label={product.reviewRate === null ? t("tin") : t("tin-initial")}
        value={percent(product.initialRate)}
      />
      {product.reviewRate !== null && (
        <Row label={t("tin-review")} value={percent(product.reviewRate, product.key === "variable" ? 3 : 2)} />
      )}
      <Row label={t("tae")} value={percent(product.tae)} className="simulator__taxes-row--subtotal" />
      <Row label={t("total-repaid")} value={money(product.totalRepaid)} />
      <Row label={t("total-interest")} value={money(product.totalInterest)} />
    </div>
  );
}

export default function InstallmentsCalculatorGirona({ onBack, onContinue }) {
  const { t, i18n } = useTranslation("installments-calculator-girona");
  const { money, percent } = useSimulatorFormat();

  const [loanAmount, setLoanAmount] = useState("");
  const [propertyPrice, setPropertyPrice] = useState("");
  const [residence, setResidence] = useState("main");
  const [termYears, setTermYears] = useState(30);

  const result = useMemo(
    () => calculateInstalmentsGirona({ loanAmount, propertyPrice, termYears, residence }),
    [loanAmount, propertyPrice, termYears, residence]
  );

  const handleDownload = async () => {
    const currentInputs = {
      loanAmount,
      propertyPrice,
      residence,
      termYears,
    };

    const currentResult =
      calculateInstalmentsGirona(
        currentInputs,
      );

    if (!currentResult) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "MONTHLY_INSTALLMENT",
      region: "GIRONA",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        GIRONA_LOAN_PARAMS.version,
      inputs:
        currentInputs,
      results:
        currentResult,
    });
  };

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "MONTHLY_INSTALLMENT",
          expectedRegion:
            "GIRONA",
        },
      );

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "loanAmount",
      )
    ) {
      setLoanAmount(
        asText(
          inputs.loanAmount,
        ),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "propertyPrice",
      )
    ) {
      setPropertyPrice(
        asText(
          inputs.propertyPrice,
        ),
      );
    }

    if (
      ["main", "second"].includes(
        inputs.residence,
      )
    ) {
      setResidence(
        inputs.residence,
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "termYears",
      )
    ) {
      setTermYears(
        clampNumber(
          inputs.termYears,
          MIN_TERM_YEARS,
          MAX_TERM_YEARS,
          30,
        ),
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Calculator size={16} />
              <span>{t("your-loan")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field simulator__field--wide">
              <FieldLabel tip={t("tips.loan-amount-input")}>{t("loan-amount-input")}</FieldLabel>
              <FieldWithIcon icon={Euro} unit="€">
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.property-price")}>{t("property-price")}</FieldLabel>
              <FieldWithIcon icon={Home} unit="€">
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={propertyPrice}
                  onChange={(e) => setPropertyPrice(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.residence")}>{t("residence")}</FieldLabel>
              <FieldWithIcon icon={KeyRound}>
                <select value={residence} onChange={(e) => setResidence(e.target.value)}>
                  <option value="main">{t("residences.main")}</option>
                  <option value="second">{t("residences.second")}</option>
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field simulator__field--slider simulator__field--wide">
              <FieldLabel tip={t("tips.term")}>{t("term")}</FieldLabel>
              <div className="simulator__slider-row">
                <input
                  type="range"
                  min={MIN_TERM_YEARS}
                  max={MAX_TERM_YEARS}
                  value={termYears}
                  onChange={(e) => setTermYears(Number(e.target.value))}
                />
                <span className="simulator__slider-value">{t("years", { count: termYears })}</span>
              </div>
              <div className="simulator__slider-limits">
                <span>{MIN_TERM_YEARS}</span>
                <span>{MAX_TERM_YEARS}</span>
              </div>
            </div>
          </div>
          <p className="simulator__hint simulator__hint--after-grid">{t("inputs-hint")}</p>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-catalonia")}</span>
          <span className="simulator__tag">{t("tag-term", { count: termYears })}</span>
          {result && <span className="simulator__tag">{t("tag-ltv", { ltv: percent(result.ltv) })}</span>}
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
        </div>

        <label className="simulator__result-label">{t("headline")}</label>
        <span className="simulator__result-value">
          {result ? money(result.products.mixed.instalmentInitial) : "— €"}
        </span>

        {!result ? (
          <p className="simulator__empty-result">{t("empty-result")}</p>
        ) : (
          <>
            <div className="simulator__rows">
              <div className="simulator__row">
                <span>{t("loan-amount")}</span>
                <span>{money(result.loanAmount)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("ltv")}</span>
                <span>{percent(result.ltv)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("loan-term")}</span>
                <span>{t("term-value", { years: result.termYears, months: result.termMonths })}</span>
              </div>
            </div>

            {PRODUCTS.map((key) =>
              result.products[key] ? (
                <ProductBox key={key} product={result.products[key]} t={t} money={money} percent={percent} />
              ) : (
                <p key={key} className="simulator__muted">
                  {t("fixed-unavailable", { years: GIRONA_LOAN_PARAMS.fixedProduct.minTermYears })}
                </p>
              )
            )}

            <p className="simulator__note">{t("loan-simulator-note")}</p>
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          <br />
          {GIRONA_LOAN_PARAMS.version}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={!result}
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
